<template>
    <master-layout pageTitle="Settings">
        <div class="settings-page">
            <div v-if="error" class="section-card">
                <div class="card-content">
                    <div class="error-row">
                        <strong>Failed to load webhooks</strong>
                        <div class="muted">{{ error }}</div>
                        <VButton appearance="outlined" connotation="cta" label="Retry" size="condensed" @click="fetchWebhooks" />
                    </div>
                </div>
            </div>

            <div v-else-if="isEmpty" class="webhooks-empty-page">
                <div class="empty-wrap">
                    <div class="empty-card">
                        <div class="card-details">
                            <h2 class="title">No Webhooks added yet</h2>
                            <p class="subtitle">Add a webhook to receive real-time event updates from your application.</p>
                        </div>
                        <VButton connotation="cta" appearance="filled" label="Add Webhook" :disabled="isLimitReached" @click="openAddModal">
                            <template #icon><VIcon :name="addOutline" /></template>
                        </VButton>
                    </div>
                </div>
            </div>

            <div v-else>
                <div class="section-card">
                    <div class="card-content">
                        <div class="list-header">
                            <h3>Webhooks</h3>
                            <span class="tooltip add-webhook-tooltip" @mouseenter="onLimitTooltipEnter" @mouseleave="showLimitTooltip = false">
                                <VButton connotation="cta" appearance="filled" label="Add Webhook" :disabled="isLimitReached" @click="openAddModal">
                                    <template #icon><VIcon :name="addOutline" /></template>
                                </VButton>
                                <span v-if="showLimitTooltip && isLimitReached" class="tooltip-text">Webhook limit reached. Maximum {{ WEBHOOK_LIMIT }} webhooks allowed.</span>
                            </span>
                        </div>
                        <CustomTable :columns="columns" :rows="sortedRows" class="custom-table" :sort-field="sortField"
                            :sort-direction="sortDirection"
                            @sort="({ field, direction }) => { sortField = field; sortDirection = direction; }">
                            <template #url="{ value }">
                                <div class="url-row">
                                    <span class="url-text" :title="value">{{ value }}</span>
                                    <VButton class="copy-btn" appearance="ghost" connotation="cta" size="condensed" @click="() => copyToClipboard(value)">
                                        <template #icon><VIcon :name="copyOutline" /></template>
                                    </VButton>
                                </div>
                            </template>

                            <template #method="{ value }">
                                <span>{{ value }}</span>
                            </template>

                            <template #scopes="{ row }">
                                <button class="link-btn" @click="viewScopes(row)">
                                    <span>View Scopes</span>
                                    <VIcon :name="eyeOutline" />
                                </button>
                            </template>

                            <template #created_at="{ value }">{{ dateFormatter(value) }}</template>
                            <template #updated_at="{ value }">{{ dateFormatter(value) }}</template>
                            <template #is_valid="{ row, value }">
                                <span class="tooltip">
                                    <span class="status-chip" :style="getChipStyle(value ? 'Active' : 'deleted')">
                                        {{ value ? "Active" : "Inactive" }}
                                    </span>
                                    <span v-if="!value" class="tooltip-text">Last validation failed at : {{ dateFormatter(row.last_validation_failed_at, true) }}</span>
                                </span>
                            </template>
                            <template #actions="{ row }">
                                <div class="actionContainer">
                                    <span class="tooltip">
                                        <VIcon :name="createOutline" class="actionIcons cursorPointer" @click="editWebhook(row)" />
                                        <span class="tooltip-text">Edit</span>
                                    </span>
                                    <span class="tooltip">
                                        <VIcon :name="trashOutline" connotation="alert" class="actionIcons cursorPointer trashIcon" @click="openDeleteModal(row)" />
                                        <span class="tooltip-text">Delete</span>
                                    </span>
                                </div>
                            </template>
                        </CustomTable>
                    </div>
                </div>
                <SignedWebhookCard />
            </div>
        </div>
        <AddWebhookModal v-model="showAddModal" :onSave="handleSave" :webhookUrl="webhookUrl"
            :selectedScopes="selectedScopes" :selectedId="selectedId ? String(selectedId) : null"
            :viewWebhookScopesWithId="viewWebhookScopesWithId" :fetchWebhooksList="fetchWebhooks" />
        <ViewScopeModal v-model="showViewScopeModal" :webhookId="selectedWebhookId ? String(selectedWebhookId) : null"
            :viewWebhookScopesWithId="viewWebhookScopesWithId" />
        <CustomWarningModal :isOpen="showDeleteModal" heading="Are you sure you want to delete this webhook?"
            description="Once you delete this webhook it will be deleted permanently" cancelText="Cancel"
            confirmText="Delete" @cancel="showDeleteModal = false" @confirm="deleteWebhook" />
    </master-layout>
</template>

<script>
import AddWebhookModal from "./AddWebhookModal.vue";
import { dateFormatter, getChipStyle } from "@/helpers/global.helpers";
import apiInterceptor from "@/shared/apiInterceptor";
import ViewScopeModal from "./ViewScopeModal.vue";
import SignedWebhookCard from "./SignedWebhookCard.vue";
import CustomWarningModal from "@/components/CustomWarningModal.vue";
import CustomTable from "@/components/CustomTable.vue";
import { useUIStore } from '@/stores/ui';
import { environment } from '@/shared/env.js';
import { useClipboard } from '@/composables/useClipboard';
export default {
    name: "Settings",
    components: { CustomTable, AddWebhookModal, ViewScopeModal, SignedWebhookCard, CustomWarningModal },
    setup() {
        const { copyToClipboard } = useClipboard();
        return { copyToClipboard };
    },
    data() {
        const METHOD_MAP = { "HTTPS POST": "POST" };
        return {
            WEBHOOK_LIMIT: 3,
            addOutline: 'plus-line',
            copyOutline: 'copy-2-line',
            createOutline: 'edit-line',
            trashOutline: 'delete-line',
            eyeOutline: 'eye-line',
            webhookUrl: null,
            selectedScopes: [],
            loading: true,
            error: null,
            showAddModal: false,
            showViewScopeModal: false,
            showDeleteModal: false,
            showLimitTooltip: false,
            limitTooltipPos: { top: 0, left: 0 },
            rows: [],
            columns: [
                { label: "URL", field: "url" },
                { label: "Method", field: "method", width: "100px" },
                { label: "Scopes", field: "scopes" },
                { label: "Date Created", field: "created_at", sortable: true },
                { label: "Last Updated", field: "updated_at", sortable: true },
                { label: "Created By", field: "created_by" },
                { label: "Status", field: "is_valid" },
                { label: "Actions", field: "actions", width: "100px" },
            ],
            METHOD_MAP,
            selectedId: null,
            selectedWebhookId: null,
            selectedRow: null,
            sortField: "created_at",
            sortDirection: "desc",
        };
    },
    computed: {
        isEmpty() { return !this.loading && !this.error && this.rows.length === 0; },
        isLimitReached() { return this.rows.length >= this.WEBHOOK_LIMIT; },
        sortedRows() {
            if (!this.sortField || !this.sortDirection) return this.rows;
            const sortKey = this.sortField;
            const sortMultiplier = this.sortDirection === "asc" ? 1 : -1;
            return [...this.rows].sort((a, b) => {
                const aRaw = a[sortKey]; const bRaw = b[sortKey];
                const aDate = aRaw ? new Date(aRaw) : null;
                const bDate = bRaw ? new Date(bRaw) : null;
                if (aDate && bDate && !isNaN(aDate) && !isNaN(bDate)) {
                    if (aDate < bDate) return -1 * sortMultiplier;
                    if (aDate > bDate) return 1 * sortMultiplier;
                    return 0;
                }
                const aString = String(aRaw ?? ""); const bString = String(bRaw ?? "");
                if (aString < bString) return -1 * sortMultiplier;
                if (aString > bString) return 1 * sortMultiplier;
                return 0;
            });
        },
    },
    methods: {
        dateFormatter,
        getChipStyle,
        showToast(message, type = "danger") {
            useUIStore().updateToast({ open: true, message, type });
        },
        onLimitTooltipEnter(event) {
            if (!this.isLimitReached) return;
            const rect = event.currentTarget.getBoundingClientRect();
            this.limitTooltipPos = {
                top: rect.bottom + 6,
                left: rect.right,
            };
            this.showLimitTooltip = true;
        },
        async fetchWebhooks() {
            this.error = null;
            try {
                useUIStore().updateLoading({ isLoading: true, message: 'Loading webhooks...' });
                const response = await apiInterceptor.get(environment("VUE_APP_APIGEE_API_URL") + "webhooks");
                this.rows = response.webhooks.map((w) => ({ ...w }));
            } catch (e) { this.error = e?.response?.data?.detail || "Unknown error"; }
            finally { useUIStore().updateLoading({ isLoading: false }); }
        },
        openAddModal() {
            if (this.isLimitReached) {
                this.showToast(`Webhook limit reached. Maximum ${this.WEBHOOK_LIMIT} webhooks allowed.`);
                return;
            }
            this.showAddModal = true;
        },
        openDeleteModal(row) { this.showDeleteModal = true; this.selectedRow = row; },
        async handleSave(data) {
            if (data.selectedScope.length < 1) throw new Error("Please select at least one scope.");
            const method = this.METHOD_MAP[data.method] ?? String(data.method || "").toUpperCase();
            const payload = { url: data.url, method, scopes: data.selectedScope };
            if (data.edit) {
                await apiInterceptor.put(environment("VUE_APP_APIGEE_API_URL") + "webhooks/" + this.selectedId, payload);
            } else {
                await apiInterceptor.post(environment("VUE_APP_APIGEE_API_URL") + "webhooks", payload);
            }
        },
        async viewWebhookScopesWithId(id) {
            return await apiInterceptor.get(environment("VUE_APP_APIGEE_API_URL") + "webhooks/" + id + "/scopes");
        },
        viewScopes(row) { this.showViewScopeModal = true; this.selectedWebhookId = row.id; },
        editWebhook(row) {
            this.webhookUrl = row.url;
            this.selectedScopes = row.selectedScopes;
            this.showAddModal = true;
            this.selectedId = row.id;
        },
        async deleteWebhook() {
            this.showDeleteModal = false;
            useUIStore().updateLoading({ isLoading: true, message: 'Deleting webhook...' });
            try {
                await apiInterceptor.delete(environment("VUE_APP_APIGEE_API_URL") + "webhooks/" + this.selectedRow?.id);
                await this.fetchWebhooks();
                useUIStore().updateToast({ open: true, message: "Webhook deleted successfully.", type: "success" });
            } catch (error) {
                useUIStore().updateToast({ open: true, message: error?.response?.data?.detail || "Failed to delete webhook", type: "danger" });
            } finally { useUIStore().updateLoading({ isLoading: false }); }
        },
    },
    watch: {
        showAddModal(val) { if (!val) { this.webhookUrl = null; this.selectedScopes = []; this.selectedId = null; } },
    },
    ionViewWillEnter() { this.fetchWebhooks(); this.showAddModal = false; },
};
</script>

<style scoped lang="scss">
.settings-page { padding: 16px; }
.webhooks-empty-page { display: flex; align-items: center; justify-content: center; height: 100%; }
.empty-wrap { height: 100%; display: flex; align-items: center; justify-content: center; width: 100%; }
.empty-card { width: 100%; border-radius: 12px; border: 1px solid var(--background-primary); background: var(--white); padding: 36px 28px; text-align: center; }
.title { font-size: 24px; font-weight: 700; color: var(--black); }
.subtitle { margin: 0 0 20px; color: var(--gray-04); font-size: 18px; font-weight: 400; }
.card-details { gap: 10px; align-items: center; display: flex; flex-direction: column; margin-bottom: 20px; }
.section-card { border-radius: 12px; border: 1px solid var(--background-primary); box-shadow: none; background: var(--white); margin-bottom: 20px; }
.card-content { padding: 20px; }
.list-header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 15px; width: 100%;
    h3 { margin: 0; font-size: 18px; color: var(--black); font-weight: 700; }
}
.status-chip { display: inline-block; padding: 4px 12px; border-radius: 16px; font-size: 12px; font-weight: 500; }
.link-btn {
    appearance: none; border: 0; background: transparent; font-weight: 600; cursor: pointer;
    display: inline-flex; gap: 6px; align-items: center; color: var(--gray-04); font-size: 20px;
    span { font-size: 14px; color: var(--purple); }
}
.actionIcons { width: 20px; height: 20px; }
.actionContainer { display: flex; align-items: center; justify-content: flex-start; gap: 15px; }
.tooltip { position: relative; display: inline-block; }
.tooltip-text {
    visibility: hidden; width: max-content; background-color: #333; color: #fff; text-align: center;
    border-radius: 6px; padding: 4px 8px; position: absolute; z-index: 1001; bottom: 100%; left: 50%;
    transform: translateX(-50%); opacity: 0; transition: opacity 0.2s ease-in-out; font-size: 12px; white-space: nowrap;
}
.tooltip:hover .tooltip-text { visibility: visible; opacity: 1; }
.custom-table { padding: 0; }
.url-row { display: flex; align-items: center; gap: 6px; }
.url-text { flex: 1; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.copy-btn { flex-shrink: 0; }
.error-row { text-align: center; padding: 20px; display: flex; flex-direction: column; align-items: center; gap: 10px; }
.muted { color: var(--gray-04); }
</style>
